import { useMemo, useState, type ReactElement } from "react";
import { Plus, Search, Users } from "lucide-react";
import { Badge, Button, EmptyState, Stack, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, ConfirmAction, SplitPane, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { useApiClient } from "@shell/apiClientContext";
import { matchesQuery } from "../common/matchesQuery";
import { useUserDirectorySearch, useUserDisplayNames } from "../users/hooks";
import { useWorkspaceDisplayNames } from "../workspaces/hooks";
import { describeApiError } from "../common/errors";
import { ExportDataButton } from "../common/ExportDataButton";
import { buildWorkspaceAssignmentsExportCsv } from "../common/exportCsv";
import {
    useCreateWorkspaceAssignment,
    useReleaseWorkspaceAssignment,
    useWorkspaceAssignments,
} from "./hooks";
import { exportWorkspaceAssignments } from "./api";
import { WorkspaceAssignmentsList } from "./WorkspaceAssignmentsList";
import {
    WorkspaceAssignmentForm,
    type WorkspaceAssignmentFormMode,
} from "./WorkspaceAssignmentForm";
import type { WorkspaceAssignmentView } from "./types";

type Mode = { kind: "idle" } | { kind: "create" } | { kind: "view"; id: string };

export function WorkspaceAssignmentsPage(): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const client = useApiClient();
    const [search, setSearch] = useState("");
    const [openOnly, setOpenOnly] = useState(true);
    const [mode, setMode] = useState<Mode>({ kind: "idle" });
    const [pendingRelease, setPendingRelease] = useState<WorkspaceAssignmentView | null>(null);

    const assignmentsQuery = useWorkspaceAssignments({
        workspaceId: "",
        userId: "",
        openOnly,
    });
    const directorySearch = useUserDirectorySearch(search);

    const createMutation = useCreateWorkspaceAssignment();
    const releaseMutation = useReleaseWorkspaceAssignment();

    const catalog = useMemo(() => assignmentsQuery.data ?? [], [assignmentsQuery.data]);

    // Resolve every userId and workspaceId on the catalog through the
    // shared single-id caches. The list itself is the full catalog (the
    // search box filters it locally by name, email, username, or id).
    const userIdsOnPage = useMemo(() => catalog.map((r) => r.userId), [catalog]);
    const { map: userIndex } = useUserDisplayNames(userIdsOnPage);

    const workspaceIdsOnPage = useMemo(() => catalog.map((r) => r.workspaceId), [catalog]);
    const { map: workspaceIndex } = useWorkspaceDisplayNames(workspaceIdsOnPage);

    // Build a map of workspaceId -> open assignment so the form can warn the
    // user when re-assigning will release the previous occupant.
    const openAssignmentsByWorkspace = useMemo(() => {
        const map = new Map<string, WorkspaceAssignmentView>();
        for (const r of catalog) {
            if (r.isOpen) map.set(r.workspaceId, r);
        }
        return map;
    }, [catalog]);

    const directoryUserIds = useMemo(() => {
        const ids = new Set<string>();
        for (const user of directorySearch.data ?? []) ids.add(user.id.toLowerCase());
        return ids;
    }, [directorySearch.data]);

    const rows = useMemo(() => {
        const q = search.trim();
        if (q === "") return catalog;
        return catalog.filter((row) => {
            if (directoryUserIds.has(row.userId.toLowerCase())) return true;
            const user = userIndex.get(row.userId) ?? null;
            const workspace = workspaceIndex.get(row.workspaceId) ?? null;
            return matchesQuery(q, [
                row.userId,
                user?.displayName,
                user?.email,
                user?.userPrincipalName,
                row.workspaceId,
                workspace?.name,
                workspace?.friendlyName,
                workspace?.floorplanElementId,
            ]);
        });
    }, [catalog, search, directoryUserIds, userIndex, workspaceIndex]);

    const selectedRecord = useMemo(() => {
        if (mode.kind !== "view") return null;
        return rows.find((r) => r.id === mode.id) ?? null;
    }, [mode, rows]);

    const formMode: WorkspaceAssignmentFormMode | null = useMemo(() => {
        if (mode.kind === "create") return { kind: "create" };
        if (mode.kind === "view" && selectedRecord !== null)
            return { kind: "view", record: selectedRecord };
        return null;
    }, [mode, selectedRecord]);

    const isSaving = createMutation.isPending || releaseMutation.isPending;
    const formError =
        mode.kind === "create" && createMutation.isError
            ? describeApiError(createMutation.error)
            : null;

    function handleCreate(payload: Parameters<typeof createMutation.mutate>[0]): void {
        // Post-save → idle (back to list). Matches the rest of the admin
        // pages: a successful mutation drops the user at the master view
        // instead of stranding them on the freshly-created record.
        createMutation.mutate(payload, {
            onSuccess: () => setMode({ kind: "idle" }),
        });
    }

    function handleReleaseRequest(record: WorkspaceAssignmentView): void {
        setPendingRelease(record);
    }

    function handleReleaseConfirmed(): void {
        if (pendingRelease === null) return;
        const record = pendingRelease;
        releaseMutation.mutate(
            { workspaceId: record.workspaceId, assignmentId: record.id },
            {
                onSuccess: () => {
                    setPendingRelease(null);
                    setMode({ kind: "idle" });
                },
            },
        );
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">Workspace Occupants</Text>
                        <Badge tone="neutral">{rows.length}</Badge>
                    </Stack>
                }
                trailing={
                    <Stack direction="row" gap={2} align="center" wrap>
                        <Search size={14} style={{ opacity: 0.55 }} />
                        <div style={{ width: 220 }}>
                            <TextInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Name, email, or user id…"
                            />
                        </div>
                        <Button
                            variant={openOnly ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setOpenOnly((v) => !v)}
                        >
                            {openOnly ? "Open only" : "All"}
                        </Button>
                        <ExportDataButton
                            filenamePrefix="workspace-occupants"
                            run={async () => {
                                const exported = await exportWorkspaceAssignments(client, {
                                    ...(openOnly ? { open: true } : {}),
                                });
                                return {
                                    csv: buildWorkspaceAssignmentsExportCsv(exported.results),
                                    truncated: exported.truncated,
                                    rowCount: exported.results.length,
                                };
                            }}
                        />
                        {!isPhone && canEdit ? (
                            <Button
                                variant="primary"
                                size="sm"
                                leadingIcon={<Plus size={16} />}
                                onClick={() => setMode({ kind: "create" })}
                            >
                                New occupant
                            </Button>
                        ) : null}
                    </Stack>
                }
            />

            <SplitPane
                activeBelowDesktop={mode.kind === "idle" ? "master" : "detail"}
                master={
                    <WorkspaceAssignmentsList
                        rows={rows}
                        workspaceIndex={workspaceIndex}
                        userIndex={userIndex}
                        selectedId={mode.kind === "view" ? mode.id : null}
                        onSelect={(row) => setMode({ kind: "view", id: row.id })}
                        isLoading={assignmentsQuery.isPending}
                        error={
                            assignmentsQuery.isError
                                ? describeApiError(assignmentsQuery.error)
                                : null
                        }
                    />
                }
                detail={
                    formMode === null ? (
                        <EmptyState
                            icon={<Users size={32} />}
                            title={
                                canEdit ? "Pick or create an occupant" : "Pick an occupant to view"
                            }
                            description={
                                canEdit
                                    ? "Open an assignment from the list to release it, or assign a user to a workspace."
                                    : "Open an assignment from the list to view its details."
                            }
                            action={
                                canEdit ? (
                                    <Button
                                        variant="primary"
                                        leadingIcon={<Plus size={16} />}
                                        onClick={() => setMode({ kind: "create" })}
                                    >
                                        New occupant
                                    </Button>
                                ) : undefined
                            }
                        />
                    ) : (
                        <WorkspaceAssignmentForm
                            mode={formMode}
                            openAssignmentsByWorkspace={openAssignmentsByWorkspace}
                            onCreate={handleCreate}
                            onRelease={handleReleaseRequest}
                            onCancel={() => setMode({ kind: "idle" })}
                            isSaving={isSaving}
                            errorMessage={formError}
                        />
                    )
                }
            />

            {isPhone && canEdit && mode.kind === "idle" ? (
                <BottomActionBar>
                    <Button
                        variant="primary"
                        leadingIcon={<Plus size={16} />}
                        onClick={() => setMode({ kind: "create" })}
                    >
                        New occupant
                    </Button>
                </BottomActionBar>
            ) : null}

            <ConfirmAction
                open={pendingRelease !== null}
                title="Release this occupant?"
                description="The workspace will become available again. The assignment record will remain in history with a release timestamp."
                confirmLabel="Release"
                destructive
                busy={releaseMutation.isPending}
                onCancel={() => setPendingRelease(null)}
                onConfirm={handleReleaseConfirmed}
            />
        </Stack>
    );
}
